import { THREE } from "../config/ThreeConfig.js";

export class TouchControls {
  constructor(game) {
    this.game = game;
    this.enabled = false;
    this._buttons = [];
    this._joystickActive = false;
    this._joystickStart = { x: 0, y: 0 };
    this._joystickCurrent = { x: 0, y: 0 };
    this._joystickKnob = null;
    this._joystickBase = null;
    this._overlay = null;
    this._aimZone = null;
    this._stickX = 0;
    this._stickY = 0;
    this._fireHeld = false;
  }

  init() {
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!hasTouch) return;

    this.enabled = true;
    this._createOverlay();
    this._createJoystick();
    this._createButtons();
    this._createAimZone();
    this._applyLayout();
    this._hideKeyboardHint();
  }

  _isLandscape() {
    return (window.innerWidth || 1) >= (window.innerHeight || 1);
  }

  _scaleFactor() {
    const minDim = Math.min(window.innerWidth || 1, window.innerHeight || 1);
    return Math.max(0.65, Math.min(1.0, minDim / 400));
  }

  _hideKeyboardHint() {
    const hint = document.getElementById('controls-hint');
    if (hint) hint.style.display = 'none';
  }

  _showKeyboardHint() {
    const hint = document.getElementById('controls-hint');
    if (hint) hint.style.display = '';
  }

  _createOverlay() {
    const overlay = document.createElement('div');
    overlay.id = 'touch-controls';
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'pointer-events:none', 'z-index:8000',
      'touch-action:none', 'user-select:none', '-webkit-user-select:none',
    ].join(';');
    document.body.appendChild(overlay);
    this._overlay = overlay;
  }

  _createJoystick() {
    const base = document.createElement('div');
    base.id = 'touch-joystick-base';
    base.style.cssText = [
      'position:absolute',
      'border-radius:50%',
      'border:2px solid rgba(0,255,255,0.3)',
      'background:rgba(0,255,255,0.08)',
      'pointer-events:auto', 'touch-action:none',
    ].join(';');

    const knob = document.createElement('div');
    knob.id = 'touch-joystick-knob';
    knob.style.cssText = [
      'position:absolute', 'top:50%', 'left:50%',
      'border-radius:50%',
      'background:rgba(0,255,255,0.4)', 'border:2px solid rgba(0,255,255,0.6)',
      'transform:translate(-50%,-50%)', 'pointer-events:none',
    ].join(';');

    base.appendChild(knob);
    this._overlay.appendChild(base);
    this._joystickBase = base;
    this._joystickKnob = knob;

    const getMaxDist = () => {
      const s = this._scaleFactor();
      const baseSize = Math.round(120 * s);
      return (baseSize / 2) * 0.7;
    };

    const onStart = (e) => {
      e.preventDefault();
      this._joystickActive = true;
      const t = e.changedTouches?.[0] || e.touches?.[0];
      if (!t) return;
      const rect = base.getBoundingClientRect();
      this._joystickStart = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      this._joystickCurrent = { x: t.clientX, y: t.clientY };
    };

    const onMove = (e) => {
      if (!this._joystickActive) return;
      e.preventDefault();
      const t = e.changedTouches?.[0] || e.touches?.[0];
      if (!t) return;
      this._joystickCurrent = { x: t.clientX, y: t.clientY };

      const maxDist = getMaxDist();
      let dx = this._joystickCurrent.x - this._joystickStart.x;
      let dy = this._joystickCurrent.y - this._joystickStart.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > maxDist) {
        dx = (dx / dist) * maxDist;
        dy = (dy / dist) * maxDist;
      }

      knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
      this._stickX = dx / maxDist;
      this._stickY = dy / maxDist;
    };

    const onEnd = (e) => {
      e.preventDefault();
      this._joystickActive = false;
      this._stickX = 0;
      this._stickY = 0;
      knob.style.transform = 'translate(-50%, -50%)';
    };

    base.addEventListener('touchstart', onStart, { passive: false });
    base.addEventListener('touchmove', onMove, { passive: false });
    base.addEventListener('touchend', onEnd, { passive: false });
    base.addEventListener('touchcancel', onEnd, { passive: false });
  }

  _createButtons() {
    const btnDefs = [
      { id: 'fire', label: 'FIRE', action: 'fire', hold: true, color: 'rgba(255,50,50,0.5)', border: 'rgba(255,50,50,0.7)' },
      { id: 'boost', label: 'BOOST', action: 'boost', hold: true, color: 'rgba(0,255,255,0.4)', border: 'rgba(0,255,255,0.6)' },
      { id: 'bomb', label: 'BOMB', action: 'bomb', hold: false, color: 'rgba(255,165,0,0.4)', border: 'rgba(255,165,0,0.6)' },
      { id: 'roll', label: 'ROLL', action: 'roll', hold: false, color: 'rgba(0,255,100,0.4)', border: 'rgba(0,255,100,0.6)' },
      { id: 'special', label: 'SPEC', action: 'special', hold: false, color: 'rgba(200,100,255,0.4)', border: 'rgba(200,100,255,0.6)' },
    ];

    for (const def of btnDefs) {
      const btn = document.createElement('div');
      btn.className = `touch-btn touch-btn-${def.id}`;
      btn.dataset.action = def.action;
      btn.dataset.hold = def.hold ? '1' : '0';
      btn.style.cssText = [
        'position:absolute',
        'border-radius:50%',
        `border:2px solid ${def.border}`, `background:${def.color}`,
        'pointer-events:auto', 'touch-action:none',
        'display:flex', 'align-items:center', 'justify-content:center',
        'font-weight:bold', 'color:#fff',
        'font-family:monospace', 'text-shadow:0 0 6px rgba(0,255,255,0.5)',
      ].join(';');
      btn.textContent = def.label;
      this._overlay.appendChild(btn);

      const action = def.action;
      const isHold = def.hold;

      btn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.game.input[action] = true;
        if (action === 'fire') this._fireHeld = true;
        btn.style.filter = 'brightness(1.8)';
      }, { passive: false });

      btn.addEventListener('touchend', (e) => {
        e.preventDefault();
        if (isHold) {
          this.game.input[action] = false;
        } else {
          setTimeout(() => { this.game.input[action] = false; }, 50);
        }
        if (action === 'fire') this._fireHeld = false;
        btn.style.filter = '';
      }, { passive: false });

      btn.addEventListener('touchcancel', (e) => {
        e.preventDefault();
        this.game.input[action] = false;
        if (action === 'fire') this._fireHeld = false;
        btn.style.filter = '';
      }, { passive: false });

      this._buttons.push(btn);
    }
  }

  _createAimZone() {
    const zone = document.createElement('div');
    zone.id = 'touch-aim-zone';
    zone.style.cssText = [
      'position:absolute',
      'pointer-events:auto', 'touch-action:none',
    ].join(';');
    this._overlay.appendChild(zone);
    this._aimZone = zone;

    zone.addEventListener('touchstart', (e) => {
      const t = e.changedTouches?.[0] || e.touches?.[0];
      if (!t) return;
      this._updateAimFromTouch(t);
    }, { passive: true });

    zone.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const t = e.changedTouches?.[0] || e.touches?.[0];
      if (!t) return;
      this._updateAimFromTouch(t);
    }, { passive: false });
  }

  _updateAimFromTouch(t) {
    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;
    const x = THREE.MathUtils.clamp(t.clientX, w * 0.02, w * 0.98);
    const y = THREE.MathUtils.clamp(t.clientY, h * 0.02, h * 0.98);
    this.game._mouseX = x;
    this.game._mouseY = y;
    this.game._mouseInitialized = true;
    this.game.input.device = 'touch';
  }

  _applyLayout() {
    if (!this.enabled || !this._overlay) return;
    const s = this._scaleFactor();
    const landscape = this._isLandscape();
    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;

    const baseSize = Math.round(120 * s);
    const knobSize = Math.round(48 * s);
    const btnSize = Math.round(landscape ? 64 * s : 56 * s);
    const fontSize = Math.round(landscape ? 11 * s : 9 * s);

    if (this._joystickBase) {
      this._joystickBase.style.width = `${baseSize}px`;
      this._joystickBase.style.height = `${baseSize}px`;
      if (landscape) {
        this._joystickBase.style.left = `${Math.round(24 * s)}px`;
        this._joystickBase.style.bottom = `${Math.round(60 * s)}px`;
      } else {
        this._joystickBase.style.left = `${Math.round(w * 0.5 - baseSize * 0.5)}px`;
        this._joystickBase.style.bottom = `${Math.round(h * 0.12)}px`;
      }
    }

    if (this._joystickKnob) {
      this._joystickKnob.style.width = `${knobSize}px`;
      this._joystickKnob.style.height = `${knobSize}px`;
    }

    if (this._aimZone) {
      if (landscape) {
        this._aimZone.style.top = '0';
        this._aimZone.style.left = '0';
        this._aimZone.style.width = '55%';
        this._aimZone.style.height = '70%';
      } else {
        this._aimZone.style.top = '0';
        this._aimZone.style.left = '0';
        this._aimZone.style.width = '100%';
        this._aimZone.style.height = '50%';
      }
    }

    const btns = this._overlay.querySelectorAll('.touch-btn');
    if (landscape) {
      const positions = {
        'touch-btn-fire': { right: Math.round(24 * s), bottom: Math.round(100 * s) },
        'touch-btn-boost': { right: Math.round(100 * s), bottom: Math.round(100 * s) },
        'touch-btn-bomb': { right: Math.round(24 * s), bottom: Math.round(176 * s) },
        'touch-btn-roll': { right: Math.round(100 * s), bottom: Math.round(176 * s) },
        'touch-btn-special': { right: Math.round(62 * s), bottom: Math.round(248 * s) },
      };
      for (const btn of btns) {
        const cls = Array.from(btn.classList).find(c => c.startsWith('touch-btn-'));
        const pos = positions[cls];
        if (!pos) continue;
        btn.style.right = `${pos.right}px`;
        btn.style.bottom = `${pos.bottom}px`;
        btn.style.left = '';
        btn.style.top = '';
      }
    } else {
      const gap = Math.round(8 * s);
      const totalBtnWidth = 5 * btnSize + 4 * gap;
      const startX = Math.max(4, Math.round((w - totalBtnWidth) / 2));
      const btnY = Math.round(h * 0.82);
      const order = ['touch-btn-fire', 'touch-btn-boost', 'touch-btn-bomb', 'touch-btn-roll', 'touch-btn-special'];
      let idx = 0;
      for (const btn of btns) {
        const cls = Array.from(btn.classList).find(c => order.includes(c));
        if (!cls) continue;
        const i = order.indexOf(cls);
        btn.style.left = `${startX + i * (btnSize + gap)}px`;
        btn.style.bottom = `${Math.round(h - btnY - btnSize)}px`;
        btn.style.right = '';
        btn.style.top = '';
        idx++;
      }
    }

    for (const btn of btns) {
      btn.style.width = `${btnSize}px`;
      btn.style.height = `${btnSize}px`;
      btn.style.fontSize = `${fontSize}px`;
    }
  }

  update() {
    if (!this.enabled) return;

    this.game.input.stickX = this._stickX;
    this.game.input.stickY = -this._stickY;
    this.game.input.left = this._stickX < -0.35;
    this.game.input.right = this._stickX > 0.35;
    this.game.input.forward = this._stickY < -0.35;
    this.game.input.backward = this._stickY > 0.35;

    if (this._joystickActive) {
      this.game.input.device = 'touch';
    }
  }

  destroy() {
    if (this._overlay) {
      this._overlay.remove();
      this._overlay = null;
    }
    this._joystickBase = null;
    this._joystickKnob = null;
    this._aimZone = null;
    this._buttons = [];
    this.enabled = false;
    this._stickX = 0;
    this._stickY = 0;
    this._fireHeld = false;
    this._joystickActive = false;
    this._showKeyboardHint();
  }
}
